/**
 * EternityPortrait — Portrait + animated halo, centered on the pineal gland.
 *
 * The halo always aligns to the third eye of the subject.
 * pinealGland: {x, y} — normalised (0-1) coordinates within the container.
 */

import { GuillocheHalo } from './GuillocheHalo';
import type { HaloConfig } from '../engine/guilloche';

interface EternityPortraitProps {
  imageSrc: string;
  alt?: string;
  width: number;
  heightRatio?: number;
  /** Normalised position of the third eye within the container */
  pinealGland: { x: number; y: number };
  /** Pixel offset for fine-tuning */
  haloTranslate?: { x: number; y: number };
  /** Halo diameter relative to portrait width */
  haloScale?: number;
  haloConfig: HaloConfig;
  /** Where bottom fade begins (0-1) */
  fadeStart?: number;
  imageScale?: number;
  imageOffsetX?: number;
  children?: React.ReactNode;
}

export function EternityPortrait({
  imageSrc,
  alt = '',
  width,
  heightRatio = 1.4,
  pinealGland,
  haloTranslate = { x: 0, y: 0 },
  haloScale = 0.85,
  haloConfig,
  fadeStart = 0.7,
  imageScale = 1,
  imageOffsetX = 0,
  children,
}: EternityPortraitProps) {
  const h = width * heightRatio;
  const haloSize = width * haloScale;

  // Scale haloTranslate proportionally — values were tuned at ~380px width
  const scaleFactor = width / 380;
  const haloPx = pinealGland.x * width + haloTranslate.x * scaleFactor;
  const haloPy = pinealGland.y * h + haloTranslate.y * scaleFactor;

  const imgScalePct = imageScale * 100;
  const imgLeft = -((imgScalePct - 100) / 2) + imageOffsetX * 100;
  const fadeHeight = 100 - fadeStart * 100 + 15;

  return (
    <div
      style={{
        position: 'relative',
        width,
        height: h,
        overflow: 'hidden',
      }}
    >
      {/* Ambient glow at pineal gland */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse 70% 50% at ${pinealGland.x * 100}% ${pinealGland.y * 100}%, rgba(197,162,88,0.10) 0%, rgba(160,130,55,0.04) 35%, rgba(10,10,10,0) 70%)`,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Halo — positioned at pineal gland */}
      <GuillocheHalo
        config={haloConfig}
        size={haloSize}
        style={{
          position: 'absolute',
          left: haloPx,
          top: haloPy,
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
        }}
      />

      {/* Portrait image */}
      <img
        src={imageSrc}
        alt={alt}
        draggable={false}
        style={{
          position: 'absolute',
          left: `${imgLeft}%`,
          bottom: 0,
          width: `${imgScalePct}%`,
          height: 'auto',
          pointerEvents: 'none',
          userSelect: 'none',
          zIndex: 2,
        }}
      />

      {/* Bottom fade */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: `${fadeHeight}%`,
          background:
            'linear-gradient(to bottom, transparent 0%, rgba(10,10,10,0.4) 30%, rgba(10,10,10,0.85) 65%, rgba(10,10,10,1) 100%)',
          pointerEvents: 'none',
          zIndex: 3,
        }}
      />

      {/* Extra layers (portal particles, etc.) */}
      {children}
    </div>
  );
}

export default EternityPortrait;
